import { useEffect, useMemo, useState } from "react";
import PageBreadcrumbs from "../../layouts/PageBreadcrumbs.js";
import { SkeletonTable } from "../../components/Skeleton.js";
import { api, ApiError } from "../../lib/api.js";
import { useTableState } from "../../lib/useTableState.js";
import TableToolbar from "../../components/TableToolbar.js";

type Health = Awaited<ReturnType<typeof api.platformHealth>>;
type Workers = Awaited<ReturnType<typeof api.platformWorkers>>;
type Heartbeat = Workers["heartbeats"][number];

function adapterStatus(info: unknown): { ok: boolean; label: string } {
  if (typeof info === "boolean") return { ok: info, label: info ? "ok" : "down" };
  if (info && typeof info === "object" && "status" in info) {
    const s = String((info as { status: unknown }).status);
    return { ok: s === "ok" || s === "healthy", label: s };
  }
  return { ok: false, label: "unknown" };
}

function formatAge(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s ago`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m ago`;
}

function formatInterval(ms?: number): string {
  if (!ms || ms <= 0) return "—";
  if (ms < 1000) return `${ms}ms`;
  const s = Math.round(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.round(s / 60);
  return `${m}m`;
}

function renderDetail(detail: Record<string, unknown> | undefined): string {
  if (!detail || typeof detail !== "object") return "—";
  const keys = Object.keys(detail);
  if (keys.length === 0) return "—";
  return keys
    .map((k) => {
      const v = detail[k];
      if (v == null) return `${k}=—`;
      if (typeof v === "object") return `${k}=${JSON.stringify(v)}`;
      return `${k}=${String(v)}`;
    })
    .join("  ");
}

export default function AdminHealth() {
  const [data, setData] = useState<Health | null>(null);
  const [workers, setWorkers] = useState<Workers | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [workersErr, setWorkersErr] = useState<string | null>(null);

  const load = () => {
    setLoading(true);
    setErr(null);
    setWorkersErr(null);
    Promise.allSettled([api.platformHealth(), api.platformWorkers()])
      .then(([h, w]) => {
        if (h.status === "fulfilled") setData(h.value);
        else setErr(h.reason instanceof ApiError ? h.reason.message : String(h.reason));
        if (w.status === "fulfilled") setWorkers(w.value);
        else setWorkersErr(w.reason instanceof ApiError ? w.reason.message : String(w.reason));
      })
      .finally(() => setLoading(false));
  };
  useEffect(() => {
    load();
    const id = setInterval(load, 15000);
    return () => clearInterval(id);
  }, []);

  const adapterRows = useMemo(() => {
    if (!data) return [] as Array<{ name: string; info: unknown; status: string }>;
    return Object.entries(data.adapters).map(([name, info]) => ({
      name,
      info,
      status: adapterStatus(info).label
    }));
  }, [data]);

  const tAdapters = useTableState(adapterRows as unknown as Array<Record<string, unknown>>, {
    searchFields: ["name", "status"],
    initialPageSize: 25
  });

  const heartbeatRows = workers?.heartbeats ?? [];
  const tHeartbeats = useTableState(heartbeatRows as unknown as Array<Record<string, unknown>>, {
    searchFields: ["name"],
    initialPageSize: 25
  });

  return (
    <div className="page">
      <PageBreadcrumbs />
      <header className="page-head">
        <div>
          <h1>Platform health</h1>
          <p>Adapter health, request metrics, tenant status rollup, and async job queue state.</p>
        </div>
        <button className="btn" type="button" onClick={load}>Refresh</button>
      </header>

      {err && <div className="alert alert-error">{err}</div>}
      {loading && <SkeletonTable rows={5} columns={4} />}

      {data && (
        <>
          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-head"><strong>Adapters</strong></div>
            <div className="card-body">
              <TableToolbar
                search={tAdapters.search}
                setSearch={tAdapters.setSearch}
                page={tAdapters.page}
                setPage={tAdapters.setPage}
                pageSize={tAdapters.pageSize}
                setPageSize={tAdapters.setPageSize}
                totalPages={tAdapters.totalPages}
                totalRows={tAdapters.totalRows}
                hideExport
                searchPlaceholder="Search adapter…"
              />
              <table className="data-table">
                <thead><tr><th>Adapter</th><th>Status</th></tr></thead>
                <tbody>
                  {(tAdapters.paged as unknown as Array<{ name: string; info: unknown }>).map(({ name, info }) => {
                    const s = adapterStatus(info);
                    return (
                      <tr key={name}>
                        <td><code>{name}</code></td>
                        <td>
                          <span className={`pill ${s.ok ? "pill-ok" : "pill-err"}`}>{s.label}</span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-head"><strong>Request metrics</strong></div>
            <div className="card-body" style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 16 }}>
              <div><div className="soft">Requests</div><div style={{ fontSize: 20 }}>{data.metrics.requestsTotal}</div></div>
              <div><div className="soft">Errors</div><div style={{ fontSize: 20 }}>{data.metrics.errorsTotal}</div></div>
              <div><div className="soft">Auth failures</div><div style={{ fontSize: 20 }}>{data.metrics.authFailures}</div></div>
              <div><div className="soft">Rate limited</div><div style={{ fontSize: 20 }}>{data.metrics.rateLimited}</div></div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-head"><strong>Tenants</strong></div>
            <div className="card-body" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16 }}>
              <div><div className="soft">Total</div><div style={{ fontSize: 20 }}>{data.tenants.total}</div></div>
              <div><div className="soft">Active</div><div style={{ fontSize: 20 }}>{data.tenants.active}</div></div>
              <div><div className="soft">Trial</div><div style={{ fontSize: 20 }}>{data.tenants.trial}</div></div>
              <div><div className="soft">Restricted</div><div style={{ fontSize: 20 }}>{data.tenants.restricted}</div></div>
              <div><div className="soft">Suspended</div><div style={{ fontSize: 20 }}>{data.tenants.suspended}</div></div>
            </div>
          </div>

          <div className="card" style={{ marginBottom: 16 }}>
            <div className="card-head"><strong>Async jobs</strong></div>
            <div className="card-body" style={{ display: "grid", gridTemplateColumns: "repeat(5, 1fr)", gap: 16 }}>
              <div><div className="soft">Total</div><div style={{ fontSize: 20 }}>{data.jobs.total}</div></div>
              <div><div className="soft">Queued</div><div style={{ fontSize: 20 }}>{data.jobs.queued}</div></div>
              <div><div className="soft">Running</div><div style={{ fontSize: 20 }}>{data.jobs.running}</div></div>
              <div><div className="soft">Failed</div><div style={{ fontSize: 20 }}>{data.jobs.failed}</div></div>
              <div><div className="soft">Dead-lettered</div><div style={{ fontSize: 20 }}>{data.jobs.deadLettered}</div></div>
            </div>
          </div>
        </>
      )}

      <div className="card">
        <div className="card-head"><strong>Workers &amp; schedulers</strong></div>
        <div className="card-body">
          {workersErr && <div className="alert alert-error" style={{ marginBottom: 12 }}>{workersErr}</div>}
          {!workers && !workersErr && <div className="soft">Loading worker heartbeats…</div>}
          {workers && workers.heartbeats.length === 0 && (
            <div className="soft">
              No worker heartbeats recorded. Workers publish heartbeats to Redis every ~30s; if this is empty, the worker
              process may not be running or Redis is not reachable from the worker.
            </div>
          )}
          {workers && workers.heartbeats.length > 0 && (
            <>
            <TableToolbar
              search={tHeartbeats.search}
              setSearch={tHeartbeats.setSearch}
              page={tHeartbeats.page}
              setPage={tHeartbeats.setPage}
              pageSize={tHeartbeats.pageSize}
              setPageSize={tHeartbeats.setPageSize}
              totalPages={tHeartbeats.totalPages}
              totalRows={tHeartbeats.totalRows}
              hideExport
              searchPlaceholder="Search worker…"
            />
            <table className="data-table">
              <thead>
                <tr>
                  <th>Worker / scheduler</th>
                  <th>Status</th>
                  <th>Last beat</th>
                  <th>Interval</th>
                  <th>Detail</th>
                </tr>
              </thead>
              <tbody>
                {(tHeartbeats.paged as unknown as Heartbeat[]).map((hb: Heartbeat) => (
                  <tr key={hb.name}>
                    <td><code>{hb.name}</code></td>
                    <td>
                      <span className={`pill ${hb.stale ? "pill-err" : "pill-ok"}`}>
                        {hb.stale ? "stale" : "fresh"}
                      </span>
                    </td>
                    <td>
                      <div>{formatAge(hb.ageMs)}</div>
                      <div className="soft" style={{ fontSize: 11 }}>
                        {new Date(hb.lastBeatAt).toLocaleString()}
                      </div>
                    </td>
                    <td>{formatInterval(hb.intervalMs)}</td>
                    <td style={{ fontFamily: "monospace", fontSize: 12 }}>{renderDetail(hb.detail)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
